/**
 * Validation Setup Component
 *
 * Main component for validation rules management with form and table
 */

import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button, Form, Modal, Table, Tag, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { ValidationRuleForm } from './helper/form';
import { ValidationOptionsModal } from './helper/optionsModal';
import { IValidationRule, IValidationRuleForm } from '../../types/validationSetup';
import type { ColumnsType } from 'antd/es/table';
import { useGetProductConfigurationJson, usePutConfigLive, usePostConfigUploadOverwrite } from '../../hooks';
import { useAllConfigOptions } from '../../hooks/configOption';
import { IChatBotLiveConfig } from '../../services';
import { incrementVersion } from '@/utils/version';

export const ValidationSetup: React.FC = () => {
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username ?? '';

  const [form] = Form.useForm<IValidationRuleForm>();
  const [showEditor, setShowEditor] = useState(false);
  const [editingValidation, setEditingValidation] = useState<IValidationRule | null>(null);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedValidation, setSelectedValidation] = useState<IValidationRule | null>(null);

  // Backend configuration
  const { data: backendConfigData, isLoading: backendConfigLoading } = useGetProductConfigurationJson();

  // Save mutation
  const { mutate: putConfigLive, isPending: isSaving } = usePutConfigLive();
  const { mutate: uploadOverwrite } = usePostConfigUploadOverwrite();

  // Fetch all configuration options
  const { data: allConfigOptionsData } = useAllConfigOptions();
  const allConfigOptions = allConfigOptionsData || [];
  const validationList: IValidationRule[] = backendConfigData?.Config.validationRules ?? [];

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      let updatedValidationList: IValidationRule[];

      if (editingValidation) {
        // Update existing validation
        updatedValidationList = validationList.map(v =>
          v.dogma_validationruleid === editingValidation.dogma_validationruleid
            ? { ...v, ...values, modifiedon: new Date().toISOString() }
            : v
        );
      } else {
        // Create new validation
        const newValidation: IValidationRule = {
          dogma_validationruleid: `temp-${Date.now()}`,
          ...values,
          dgma_options: [],
          statecode: 0,
          createdon: new Date().toISOString(),
        } as IValidationRule;
        updatedValidationList = [...validationList, newValidation];
      }

      // Save to backend configuration
      const payload: IChatBotLiveConfig = {
        Config: {
          ...(backendConfigData?.Config ?? {} as any),
          validationRules: updatedValidationList,
          lastUpdated: new Date().toISOString(),
          lastUpdatedBy: currentUserEmail,
        },
        Version: incrementVersion(backendConfigData?.Version)
      };

      putConfigLive(payload, {
        onSuccess: () => {
          uploadOverwrite({ body: payload, version: payload.Version });
          setShowEditor(false);
          form.resetFields();
          setEditingValidation(null);
        },
      });
    } catch (error) {
    }
  };

  const handleEdit = (validation: IValidationRule) => {
    setEditingValidation(validation);
    form.setFieldsValue({
      dogma_name: validation.dogma_name,
      dogma_min: validation.dogma_min,
      dogma_max: validation.dogma_max,
    });
    setShowEditor(true);
  };

  const handleDelete = async (validationId: string) => {
    const updatedValidationList = validationList.filter(
      v => v.dogma_validationruleid !== validationId
    );

    // Save to backend configuration
    const payload: IChatBotLiveConfig = {
      Config: {
        ...(backendConfigData?.Config ?? {} as any),
        validationRules: updatedValidationList,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: currentUserEmail,
      },
      Version: incrementVersion(backendConfigData?.Version)
    };

    putConfigLive(payload, {
      onSuccess: () => {
        uploadOverwrite({ body: payload, version: payload.Version });
      },
    });
  };

  const handleCancel = () => {
    setShowEditor(false);
    form.resetFields();
    setEditingValidation(null);
  };

  const handleAddNew = () => {
    setEditingValidation(null);
    form.resetFields();
    setShowEditor(true);
  };

  const handleSetupOptions = (validation: IValidationRule) => {
    setSelectedValidation(validation);
    setShowOptionsModal(true);
  };

  const handleCloseOptionsModal = () => {
    setShowOptionsModal(false);
    setSelectedValidation(null);
  };

  const handleSaveOptions = (options: string[]) => {
    if (!selectedValidation) return;

    const updatedValidationList = validationList.map(v =>
      v.dogma_validationruleid === selectedValidation.dogma_validationruleid
        ? { ...v, dgma_options: options, modifiedon: new Date().toISOString() }
        : v
    );

    // Save to backend configuration
    const payload: IChatBotLiveConfig = {
      Config: {
        ...(backendConfigData?.Config ?? {} as any),
        validationRules: updatedValidationList,
      },
      Version: incrementVersion(backendConfigData?.Version)
    };

    putConfigLive(payload, {
      onSuccess: () => {
        uploadOverwrite({ body: payload, version: payload.Version });
        handleCloseOptionsModal();
      },
    });
  };

  const columns: ColumnsType<IValidationRule> = [
    {
      title: 'Validation Name',
      dataIndex: 'dogma_name',
      key: 'dogma_name',
      width: 200,
    },
    {
      title: 'Min',
      dataIndex: 'dogma_min',
      key: 'dogma_min',
      width: 100,
      render: (value) => value ?? '-',
    },
    {
      title: 'Max',
      dataIndex: 'dogma_max',
      key: 'dogma_max',
      width: 100,
      render: (value) => value ?? '-',
    },
    {
      title: 'Options',
      dataIndex: 'dgma_options',
      key: 'dgma_options',
      width: 120,
      render: (options: string[]) => (
        <Tag color="blue">
          {options?.length || 0} option{options?.length !== 1 ? 's' : ''}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'statecode',
      key: 'statecode',
      width: 100,
      render: (statecode: number) => (
        <Tag color={statecode === 0 ? 'green' : 'default'}>
          {statecode === 0 ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            className='text-primary'
            icon={<SettingOutlined />}
            onClick={() => handleSetupOptions(record)}
            size="small"
            title="Setup Options"
          >
            Setup
          </Button>
          <Button
            type="link"
            className='text-primary'
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Delete Validation Rule"
            description="Are you sure you want to delete this validation rule?"
            onConfirm={() => handleDelete(record.dogma_validationruleid)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className='bg-white rounded-lg'>
        <div className="flex h-full items-center justify-between px-4 py-4 border-b border-neutral-400 gap-x-4 border-0">
          <div>
            <h1 className='text-lg font-semibold text-text-primary'>Product Validation Rules</h1>
            <p className='text-sm text-text-secondary'>Configure validation rules for product options</p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
          >
            Add Validation Rule
          </Button>
        </div>

        <div className="p-4">
          <Table
            columns={columns}
            dataSource={validationList}
            rowKey="dogma_validationruleid"
            loading={backendConfigLoading}
            scroll={{ x: 900 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} rules`,
            }}
          />
        </div>
      </div>

      <Modal
        title={editingValidation ? 'Edit Validation Rule' : 'Add Validation Rule'}
        open={showEditor}
        onCancel={handleCancel}
        onOk={handleSave}
        okText="Save"
        cancelText="Cancel"
        width={600}
        confirmLoading={isSaving}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <ValidationRuleForm />
        </Form>
      </Modal>

      <ValidationOptionsModal
        open={showOptionsModal}
        validationName={selectedValidation?.dogma_name || ''}
        options={selectedValidation?.dgma_options || []}
        allConfigOptions={allConfigOptions}
        onClose={handleCloseOptionsModal}
        onSave={handleSaveOptions}
        loading={isSaving}
      />
    </>
  );
};
